import React from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { openWhatsApp } from "../../lib/whatsapp";

const PRODUCTS = [
  {
    id: "bold-green",
    name: "Bold Green",
    grade: "8mm+",
    subtitle: "The estate benchmark",
    price: "Enquire",
    img:
      "https://images.unsplash.com/photo-1701166627787-12d9fdd437cc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHwyfHxncmVlbiUyMGNhcmRhbW9tJTIwcG9kc3xlbnwwfHx8fDE3ODU0OTM3NTJ8MA&ixlib=rb-4.1.0&q=85",
    notes: [
      "Deep emerald pods",
      "Bright, camphor-forward aroma",
      "For roasters, blenders, retail packs",
    ],
  },
  {
    id: "premium-ageb",
    name: "Premium AGEB",
    grade: "7.5–8mm",
    subtitle: "Auction-grade extra bold",
    price: "Enquire",
    img:
      "https://images.unsplash.com/photo-1716540103530-cc33cdd20cde?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjBmb29kJTIwcGFja2FnaW5nfGVufDB8fHx8MTc4NTQ5Mzc2Mnww&ixlib=rb-4.1.0&q=85",
    notes: [
      "Uniform size, high oil %",
      "Long shelf, colour-stable",
      "Preferred for GCC & MENA",
    ],
  },
  {
    id: "export-quality",
    name: "Export Quality",
    grade: "6.5–7mm",
    subtitle: "Container-lot ready",
    price: "Enquire",
    img:
      "https://images.unsplash.com/photo-1763558049571-a6d12a85472e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHw0fHxncmVlbiUyMGNhcmRhbW9tJTIwcG9kc3xlbnwwfHx8fDE3ODU0OTM3NTJ8MA&ixlib=rb-4.1.0&q=85",
    notes: [
      "Clean, well-cured lots",
      "Vacuum &/or jute packing",
      "MOQ 100 kg · scalable",
    ],
  },
];

export const Products = () => {
  return (
    <section
      id="products"
      data-testid="products-section"
      className="relative bg-cream py-28 md:py-40 border-t border-charcoal/10"
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <div className="flex items-end justify-between mb-14 md:mb-20 gap-6 flex-wrap">
          <div>
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7 }}
              className="font-sans text-[11px] uppercase tracking-[0.32em] text-charcoal/60 mb-6 flex items-center gap-3"
            >
              <span className="text-gold">§</span> Our Products
            </motion.div>
            <motion.h2
              initial={{ opacity: 0, y: 25 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 1, ease: [0.65, 0, 0.35, 1] }}
              className="font-serif text-5xl md:text-6xl lg:text-7xl text-charcoal font-light leading-[0.98]"
            >
              Three grades. <br />
              <span className="italic text-forest">One obsession.</span>
            </motion.h2>
          </div>
          <motion.p
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, delay: 0.15 }}
            className="max-w-[38ch] font-sans text-charcoal/65 text-base md:text-[17px] leading-relaxed"
          >
            We keep the catalogue small on purpose. Each grade is cupped,
            graded and packed to spec — no in-betweens, no filler lots.
          </motion.p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {PRODUCTS.map((p, i) => (
            <motion.article
              key={p.id}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{
                duration: 0.9,
                delay: i * 0.1,
                ease: [0.65, 0, 0.35, 1],
              }}
              className="group relative flex flex-col bg-cream border border-charcoal/10 rounded-sm overflow-hidden hover:border-forest/40 transition-colors"
              data-testid={`product-card-${p.id}`}
            >
              {/* Clipped image with spotlight */}
              <div className="relative aspect-[4/5] overflow-hidden bg-forest/10">
                <img
                  src={p.img}
                  alt={p.name}
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-1000 ease-out group-hover:scale-[1.08]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-forestDeep/70 via-forestDeep/15 to-forestDeep/30" />
                <div className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 bg-[radial-gradient(ellipse_at_center,rgba(201,162,39,0.22),transparent_65%)]" />
                <span className="absolute top-4 left-4 font-serif italic text-gold text-lg drop-shadow-[0_1px_2px_rgba(0,0,0,0.55)]">
                  0{i + 1}
                </span>
                <span className="absolute top-4 right-4 font-sans text-[10px] uppercase tracking-[0.28em] bg-cream/85 backdrop-blur px-2.5 py-1 text-forest rounded-full">
                  {p.grade}
                </span>
              </div>

              {/* Body */}
              <div className="p-6 md:p-8 flex-1 flex flex-col">
                <h3 className="font-serif text-3xl md:text-4xl text-charcoal">
                  {p.name}
                </h3>
                <p className="font-serif italic text-forest text-lg mt-1">
                  {p.subtitle}
                </p>
                <ul className="mt-6 space-y-2 font-sans text-charcoal/70 text-sm">
                  {p.notes.map((n) => (
                    <li key={n} className="flex items-start gap-3">
                      <span className="mt-[9px] h-[3px] w-3 bg-gold shrink-0" />
                      <span>{n}</span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() =>
                    openWhatsApp(
                      `Hello, I'd like to enquire about the ${p.name} grade (${p.grade}) cardamom.`
                    )
                  }
                  data-testid={`product-enquire-${p.id}`}
                  className="mt-8 group/btn inline-flex items-center justify-between gap-3 rounded-full bg-forest text-cream px-5 py-3.5 text-[11px] uppercase tracking-[0.28em] font-medium hover:bg-forestDeep transition-colors w-full"
                >
                  <span>Enquire on WhatsApp</span>
                  <ArrowUpRight
                    size={16}
                    className="transition-transform duration-500 group-hover/btn:rotate-45"
                  />
                </button>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Products;
